import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, Shield, Users, Video, Moon, Sun, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(false);

  // Email verification states
  const [showVerificationButton, setShowVerificationButton] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  // OTP Modal states
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);


  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [tempCredentials, setTempCredentials] = useState<{ email: string; password: string } | null>(null);


  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 2FA Setup states
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [twoFactorQRCode, setTwoFactorQRCode] = useState<string>('');
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('');
  const [twoFactorSetupStep, setTwoFactorSetupStep] = useState<'initial' | 'verify'>('initial');
  const [twoFactorSetupError, setTwoFactorSetupError] = useState<string | null>(null);
  const [twoFactorSetupLoading, setTwoFactorSetupLoading] = useState(false);

  const [ssoEnabled, setSsoEnabled] = useState(false);

  const navigate = useNavigate();
  const { login, verifyOTP, resendOTP, loading: authLoading, error: authError, clearError, verifyTwoFactorToken, setupTwoFactor, verifyAndEnableTwoFactor, changePassword } = useAuth();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sso/config`)
      .then((r) => r.json())
      .then((d) => setSsoEnabled(!!d.enabled))
      .catch(() => { });
  }, []);


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowVerificationButton(false);
    clearError();

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setIsLoading(true);

    try {
      await login(email.toLowerCase(), password, "", rememberMe);
      setIsLoading(false);
      navigate('/');
    } catch (err: any) {
      setIsLoading(false);

      if (err.requiresPasswordChange) {
        setError(err.message);
        setTempCredentials({ email, password });
        // Show password change button in error message
        setError(err.message);
        setShowPasswordChangeModal(true);
      } else if (err.requiresTwoFactorSetup) {
        setError(err.message);
        setTempCredentials({ email, password });
        setShow2FASetupModal(true);
      } else if (err.requires2FA) {
        setTempCredentials({ email, password });
        setShow2FAModal(true);
      } else if (err.requiresVerification) {
        setError(err.message);
        setShowVerificationButton(true);
        setVerificationEmail(email);
      } else {
        setError(err.message);
        setShowVerificationButton(false);
      }
    }
  };



  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setPasswordLoading(true);

    try {
      await changePassword(password, newPassword);
      setPasswordMessage('Password changed successfully!');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Close modal and attempt login again
      setTimeout(() => {
        setShowPasswordChangeModal(false);
        handleLogin(new Event('submit') as any);
      }, 1500);
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setTwoFactorSetupLoading(true);
      setTwoFactorSetupError(null);

      if (!tempCredentials) return;

      // Use the user ID from auth context or temp credentials
      const { qrCodeDataURL } = await setupTwoFactor();
      setTwoFactorQRCode(qrCodeDataURL);
      setTwoFactorSetupStep('initial');
    } catch (error: any) {
      setTwoFactorSetupError('Failed to setup 2FA: ' + error.message);
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const handleVerify2FASetup = async () => {
    if (twoFactorVerificationCode.length !== 6) {
      setTwoFactorSetupError('Please enter a 6-digit code');
      return;
    }

    try {
      setTwoFactorSetupLoading(true);
      setTwoFactorSetupError(null);

      await verifyAndEnableTwoFactor(twoFactorVerificationCode);

      // Close modal and attempt login again
      setTwoFactorVerificationCode('');
      setShow2FASetupModal(false);

      if (tempCredentials) {
        handleLogin(new Event('submit') as any);
      }
    } catch (error: any) {
      setTwoFactorSetupError('Invalid verification code: ' + error.message);
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempCredentials) return;

    setIsLoading(true);
    setError(null);

    try {
      await login(
        tempCredentials.email,
        tempCredentials.password,
        twoFactorToken // Pass the 2FA token to login
      );
      setShow2FAModal(false);
      navigate('/');
    } catch (err: any) {
      if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to verify 2FA code');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifyEmailClick = async () => {
    setOtpError(null);
    setOtpMessage(null);

    try {
      await resendOTP(verificationEmail, true);
      setShowOTPModal(true);

      // setOtpMessage('New OTP sent to your email.');
      // Clear success message after 5 seconds
      setTimeout(() => setOtpMessage(null), 5000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleResendOTP = async () => {
    setOtpError(null);

    try {
      const response = await resendOTP(verificationEmail, false);

      // Always show message for resend action
      setOtpMessage('OTP resent successfully.');

      // Start 60-second cooldown
      setResendDisabled(true);
      setCountdown(60);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Hide success message after 5 seconds
      setTimeout(() => setOtpMessage(null), 5000);
    } catch (err: any) {
      setOtpError(err.message);
    }
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    clearError();

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setOtpError('Please enter the complete 6-digit OTP.');
      return;
    }

    setOtpLoading(true);

    try {
      await verifyOTP(verificationEmail, otpString);
      setOtpLoading(false);
      setShowOTPModal(false);
      setShowVerificationButton(false);

      // Reset OTP fields
      setOtp(['', '', '', '', '', '']);

      // Show success message
      setError(null);
      setOtpMessage('Email verified successfully! Please login again.');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setOtpMessage(null);
      }, 3000);
    } catch (err: any) {
      setOtpLoading(false);
      setOtpError(err.message);
    }
  };


  const features = [
    { icon: <Video className="w-5 h-5" />, text: "HD Video Conferencing" },
    { icon: <Users className="w-5 h-5" />, text: "Team Collaboration" },
    { icon: <Shield className="w-5 h-5" />, text: "Enterprise Security" },
  ];

  return (
    <div className={`min-h-screen w-full flex ${darkMode ? 'bg-gradient-to-br from-[#181824] to-[#232336]' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
      {/* Left: Hero Section (60%) */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden" style={{ flexBasis: '60%' }}>
        {/* Background Image */}
        <img
          src="/Login.avif"
          alt="Vela Login Visual"
          className="w-full h-full object-cover object-center absolute inset-0"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-pink-900/80" />

        {/* Logo */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-12">
          <div className="text-center">
            <img src="/Vela-Icon.png" alt="Vela Logo" className="w-80 h-80 mx-auto drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* Right: Login Form (40%) */}
      <div className={`flex flex-col justify-center items-center w-full lg:w-[40%] min-h-screen px-8 pt-12 relative ${darkMode ? 'bg-[#232336]' : 'bg-white'}`}>
        <div className="w-full max-w-md mx-auto">
          {/* Theme Toggle */}
          <div className="absolute top-6 right-6">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-3 rounded-xl transition-all duration-200 ${darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-white" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="relative inline-block">
              <img src="/Vela-Icon.png" alt="Vela Logo" className="w-23 h-23 mx-auto mb-4" />
              {/* <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div> */}
            </div>
            {/* <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Welcome to <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">Vela</span>
            </h1> */}
          </div>

          {/* Form Header */}
          <div className="text-left mb-8">
            <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sign In</h2>
            <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Welcome back! Please enter your details.</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                </div>
                <input
                  type="email"
                  className={`w-full pl-10 pr-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full pl-10 pr-12 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  ) : (
                    <Eye className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className={`w-4 h-4 text-pink-500 rounded focus:ring-pink-500 focus:ring-2 ${darkMode ? 'bg-white/10 border-white/20' : 'bg-gray-100 border-gray-300'}`}
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <span className={`ml-2 text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Remember me</span>
              </label>
              <a href="/forgot-password" className={`text-sm font-medium transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                Forgot password?
              </a>
            </div>

            {/* Success Message (for OTP verification) */}
            {otpMessage && !showOTPModal && (
              <div className={`p-4 border rounded-xl ${darkMode ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>{otpMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message with Verify Button */}
            {/* Error Message with Action Buttons */}
            {error && (
              <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertCircle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{error}</p>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {showVerificationButton && (
                        <button
                          type="button"
                          onClick={handleVerifyEmailClick}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                        >
                          <Mail className="w-4 h-4" />
                          Verify Email
                        </button>
                      )}

                      {error?.includes('Password expired') && (
                        <button
                          type="button"
                          onClick={() => setShowPasswordChangeModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                        >
                          <Lock className="w-4 h-4" />
                          Change Password
                        </button>
                      )}

                      {error?.includes('Two-factor authentication required') && (
                        <button
                          type="button"
                          onClick={() => setShow2FASetupModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white text-sm font-medium rounded-lg hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                        >
                          <Shield className="w-4 h-4" />
                          Enable 2FA
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* SSO Sign In */}
          {ssoEnabled && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${darkMode ? 'border-white/20' : 'border-gray-200'}`} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-3 ${darkMode ? 'bg-[#232336] text-white/50' : 'bg-white text-gray-500'}`}>or</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/sso/login`; }}
                className={`w-full font-semibold py-4 px-6 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2 ${darkMode ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <Shield className="w-5 h-5 text-blue-600" />
                Sign in with SSO
              </button>
            </>
          )}

          {/* Register Link  Enable it if signup reauired in future*/}.
          {/* <div className="mt-8 text-center">
            <span className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Don't have an account? </span>
            <a href="/register" className={`font-medium text-sm transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
              Sign up for free
            </a>
          </div>   */}
        </div>
        <footer className={`mt-12 text-center text-xs ${darkMode ? 'text-white/50' : 'text-gray-400'}`}>
          <p>© {new Date().getFullYear()} by VELA Technologies LLC. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="https://www.vela.business/privacy-policy" className={`hover:underline ${darkMode ? 'text-white/50 hover:text-white/80' : 'text-gray-400 hover:text-gray-600'}`}>Privacy Policy</a>
            <span>·</span>
            <a href="https://www.vela.business/terms-of-service" className={`hover:underline ${darkMode ? 'text-white/50 hover:text-white/80' : 'text-gray-400 hover:text-gray-600'}`}>Terms & Conditions</a>
          </div>
        </footer>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md ${darkMode ? 'bg-[#232336]' : 'bg-white'} rounded-2xl shadow-2xl p-8 relative`}>
            {/* Close Button */}
            <button
              onClick={() => setShowOTPModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            >
              <svg className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Verify Your Email</h3>
              <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                We've sent a 6-digit verification code to <span className="font-medium">{verificationEmail}</span>
              </p>
            </div>

            {/* OTP Form */}
            <form onSubmit={handleOTPVerification} className="space-y-6">
              {/* OTP Input Fields */}
              <div>
                <label className={`block font-medium mb-3 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Enter Verification Code</label>
                <div className="flex gap-3 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className={`w-12 h-12 text-center text-lg font-semibold rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      placeholder="0"
                    />
                  ))}
                </div>
              </div>

              {/* OTP Success Message */}
              {otpMessage && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                        {otpMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* OTP Error Message */}
              {otpError && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{otpError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Verify Button */}
              <button
                type="submit"
                disabled={otpLoading}
                className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {otpLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Email
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Resend OTP Link */}
            <div className="mt-6 text-center">
              <span className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Didn't receive the code? </span>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendDisabled}
                className={`font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                {resendDisabled ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md ${darkMode ? 'bg-[#2c2c3a]' : 'bg-white'} rounded-2xl shadow-2xl p-8 relative`}>
            <button
              onClick={() => setShowPasswordChangeModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-all duration-200 ${darkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
            >
              <svg className={`w-5 h-5`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Change Password
              </h3>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className={`block mb-2 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/10 text-white placeholder-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'} border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                    placeholder="Enter current password"
                    value={password}
                    disabled
                  />
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className={`block mb-2 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/10 text-white placeholder-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'} border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded ${darkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password */}
              <div>
                <label className={`block mb-2 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/10 text-white placeholder-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'} border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded ${darkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {passwordMessage && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>{passwordMessage}</p>
                </div>
              )}

              {passwordError && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{passwordError}</p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordChangeModal(false)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${darkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg font-medium hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {passwordLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2FA Setup Modal */}
      {show2FASetupModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md ${darkMode ? 'bg-[#2c2c3a]' : 'bg-white'} rounded-2xl shadow-2xl p-8 relative`}>
            <button
              onClick={() => setShow2FASetupModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-all duration-200 ${darkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
            >
              <svg className={`w-5 h-5`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Setup Two-Factor Authentication
              </h3>
            </div>

            <div className="space-y-6">
              {twoFactorSetupStep === 'initial' ? (
                <>
                  <div className="text-center">
                    <p className={`text-sm mb-4 ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                      Scan this QR code with your authenticator app
                    </p>
                    {twoFactorQRCode ? (
                      <div className="inline-block p-4 bg-white rounded-xl">
                        <img
                          src={twoFactorQRCode}
                          alt="2FA QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={handleSetup2FA}
                        disabled={twoFactorSetupLoading}
                        className="w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg font-medium hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50"
                      >
                        {twoFactorSetupLoading ? 'Generating QR Code...' : 'Generate QR Code'}
                      </button>
                    )}
                  </div>
                  {twoFactorQRCode && (
                    <button
                      onClick={() => setTwoFactorSetupStep('verify')}
                      className="w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg font-medium hover:scale-105 transition-all duration-200 shadow-lg"
                    >
                      Next
                    </button>
                  )}
                </>
              ) : (
                <>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleVerify2FASetup();
                    }}
                  >
                    <div>
                      <label className={`block mb-2 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                        Enter Verification Code
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        className={`w-full rounded-lg px-4 py-3 text-center text-2xl tracking-wider transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                        value={twoFactorVerificationCode}
                        onChange={(e) =>
                          setTwoFactorVerificationCode(
                            e.target.value.replace(/\D/g, '').slice(0, 6)
                          )
                        }
                        placeholder="000000"
                      />
                    </div>

                    {twoFactorSetupError && (
                      <div className={`p-4 border rounded-xl mt-4 ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                        <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{twoFactorSetupError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={twoFactorVerificationCode.length !== 6 || twoFactorSetupLoading}
                      className={`w-full mt-4 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${twoFactorVerificationCode.length === 6
                        ? 'bg-gradient-to-r from-pink-600 to-red-500 text-white hover:scale-105 shadow-lg'
                        : `${darkMode ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-400'} cursor-not-allowed`
                        }`}
                    >
                      {twoFactorSetupLoading ? 'Verifying...' : 'Verify and Enable'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md ${darkMode ? 'bg-[#232336]' : 'bg-white'} rounded-2xl shadow-2xl p-8 relative`}>
            {/* Close Button */}
            <button
              onClick={() => setShow2FAModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                }`}
            >
              <svg className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Two-Factor Authentication
              </h3>
              <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            {/* 2FA Form */}
            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div>
                <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>
                  Authentication Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={twoFactorToken}
                  onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`w-full px-4 py-4 text-center text-lg font-semibold rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                  placeholder="Enter 6-digit code"
                />
              </div>

              {error && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || twoFactorToken.length !== 6}
                className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
